"use client";
import dynamic from "next/dynamic";
import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { StripeWrapper } from "@stadium/services/stripe";
import ZendeskIconPositionAdjuster from "@stadium/services/zendesk/icon-position-adjuster";

import { useComponentLifecycle } from "@stadium/hooks/lib/use-component-lifecycle";
import { useEventLocationContext } from "@stadium/services/analytics/context/event-location";
import useActionEvents from "@stadium/services/analytics/hooks/use-action-events";

import Cart from "@components/store/cart";
import { useStoreContext } from "@components/store/provider";
import CheckoutProvider from "@contexts/checkout-context";
import { useWorkspaceContext } from "@contexts/workspace-context";

import CartListingProvider from "@contexts/cart-listing-context";
import { CurrentUserContext } from "@contexts/current-user-provider";
import GlobalCheckoutProvider from "@stadium/checkout/src/contexts/checkout";
import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

const KitItemsProvider = dynamic(() => import("@contexts/kit-items"));
const KitItems = dynamic(() => import("@components/store/cart/kit-items"));
const KitLayout = dynamic(() => import("@components/layout/kit"));

import {
  getCartOrder,
  getCartOrderFetched,
  getCartItemsCount,
  getCartOrderBagTotal,
} from "@selectors/cart";
import { getIsKitStore, getStore, getStoreBalance } from "@selectors/store";
import { updateCartOrder } from "@slices/cart";

import { ZENDESK_POSITION_BOTTOM } from "@constants/app-defaults";
import { STRIPE_API_KEY } from "@constants/env-variables";
import { CART_RESPONSE_TYPE } from "@constants/orders/cart";
import usePopulateLocalItems from "@hooks/cart/use-populate-local-items";

const emptyObj = {};

const CartPage = (props = emptyObj) => {
  const { workspaceSlug } = useWorkspaceContext();

  const dispatch = useDispatch();

  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { currentUserData } = useContext(CurrentUserContext);
  const storeSpecificBalance = useSelector(getStoreBalance);
  const orderData = useSelector(getCartOrder);
  const orderFetched = useSelector(getCartOrderFetched);
  const totalItems = useSelector(getCartItemsCount);
  const totalBagValue = useSelector(getCartOrderBagTotal);

  const { location, services, baseEventLocationProperties } = useEventLocationContext();
  const { trackPage } = useActionEvents(services);

  const { updateLocalCartUseStoreSpecificPoints } = usePopulateLocalItems();
  const store = useSelector(getStore);
  const isKitShop = useSelector(getIsKitStore);
  const { setPageState, resetPageState, storeCode: slug } = useStoreContext();

  const cartPageViewTrackedRef = useRef(false);

  const postOrderUpdateCallback = (order) => dispatch(updateCartOrder(order));

  useEffect(() => {
    setPageState((state) => ({
      ...state,
      withMenu: false,
      source: "cart",
      withCart: false,
      showFooter: false,
      showHeader: !isKitShop,
    }));
  }, [isKitShop]);

  useEffect(() => {
    if (!orderFetched || cartPageViewTrackedRef.current) {
      return;
    }
    cartPageViewTrackedRef.current = true;
    trackPage({
      page: location,
      total_items: totalItems,
      total_bag_value: totalBagValue,
      ...baseEventLocationProperties,
    });
    // Page view should fire once per cart load, not on quantity/total changes (totalItems/totalBagValue).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional snapshot at first ready state
  }, [orderFetched, trackPage, location, baseEventLocationProperties]);

  useComponentLifecycle({
    onUnmount: resetPageState,
  });

  return (
    <ZendeskIconPositionAdjuster bottomOffset={ZENDESK_POSITION_BOTTOM}>
      <GlobalCheckoutProvider
        isSelfOrder
        currentUserData={currentUserData}
        storeSpecificBalance={storeSpecificBalance}
        orderData={orderFetched ? orderData : emptyObj}
        postOrderUpdateCallback={postOrderUpdateCallback}
        orderResponseType={CART_RESPONSE_TYPE}
        //update useStoreSpecificPoint in case of logged in user preview mode
        customOrderUpdateFn={(order) => {
          if (order.hasOwnProperty("useStoreSpecificPoint"))
            updateLocalCartUseStoreSpecificPoints(order.useStoreSpecificPoint);
        }}
        workspaceSlug={workspaceSlug}
      >
        {store?.isKitShop ? (
          <StripeWrapper stripeKey={STRIPE_API_KEY}>
            <BottomIconHideWrapper hideIcon={isMobileDevice}>
              <KitItemsProvider>
                <KitLayout>
                  <KitItems {...props} />
                </KitLayout>
              </KitItemsProvider>
            </BottomIconHideWrapper>
          </StripeWrapper>
        ) : (
          <StripeWrapper stripeKey={STRIPE_API_KEY}>
            <BottomIconHideWrapper hideIcon={isMobileDevice}>
              <CheckoutProvider canShowCartUpdatedMessage>
                <CartListingProvider>
                  <Cart slug={slug} {...props} />
                </CartListingProvider>
              </CheckoutProvider>
            </BottomIconHideWrapper>
          </StripeWrapper>
        )}
      </GlobalCheckoutProvider>
    </ZendeskIconPositionAdjuster>
  );
};

export default CartPage;
